import {useEffect, useMemo, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import TicketsService from '../../../../services/ticket.service'
import {KTCard, KTCardBody, KTSVG} from '../../../../_metronic/helpers'
import TicketsFilters, {TicketsFiltersValues} from './TicketsFilters'
import EscalateTicket from './EscalateTicket'
import UpdateTicket from './UpdateTicket'
import {User} from './TicketDetails'

export interface TicketResponse {
  skip: number
  take: number
  tickets: Ticket[]
  total: number
}

export interface Ticket {
  id: string
  title: string
  description: string
  priority: string
  productType: string
  providerId: number
  statusName: string
  updatedAt: string
  user: User
  createdAt?: string
  userId: string
  assignedTo: string
  closedAt: string
  commentsCount: number
  creator: User
  orderId: string
}

const ListTicket = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<TicketResponse | null>(null)
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(10)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<TicketsFiltersValues>({})
  const [providers, setProviders] = useState<any[]>([])
  const [providerIdToName, setProviderIdToName] = useState<Record<number, string>>({})
  const [sortBy, setSortBy] = useState<'id' | 'createdAt'>('id')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const userId = searchParams.get('userId')

  const fetchData = async () => {
    const params: any = {
      skip,
      take,
      search: search || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      statusName: filters.statusName || undefined,
      assignedTo: filters.assignedTo || undefined,
      priority: filters.priority || undefined,
      providerId: filters.providerId || undefined,
      productType: filters.productType || undefined,
      title: filters.title || undefined,
      userId: filters.userId || undefined,
    }
    if (userId) {
      params.userId = userId
    }
    const response = await TicketsService.getTickets(params)
    console.log(response)
    setData(response.data)
    return response.data
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, take, search, userId, filters])

  // console.log('data:', data)

  const totalPages = useMemo(() => (data ? Math.ceil(data.total / take) : 0), [data, take])

  const handleSort = (field: 'id' | 'createdAt') => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(field); setSortDir('desc') }
  }

  const sortedTickets = data
    ? [...data.tickets].sort((a, b) => {
        if (sortBy === 'id') {
          return sortDir === 'asc' ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)
        }
        const da = new Date(a.createdAt || a.updatedAt).getTime()
        const db = new Date(b.createdAt || b.updatedAt).getTime()
        return sortDir === 'asc' ? da - db : db - da
      })
    : []

  const handlePriorityColor = (priority: string) => {
    if (priority == 'High') return 'badge-pill  badge badge-light-danger'
    else if (priority == 'Medium') return ' badge-pill badge badge-light-warning'
    else if (priority == 'Low') return ' badge-pill badge badge-light-success'
    else return ' badge-pill badge badge-light'
  }

  const handleProductColor = (productType: string) => {
    if (productType == 'PrepaidCards') return ' badge-pill badge badge-light-primary'
    else if (productType == 'InternationalEsim') return ' badge-pill badge badge-light-info'
    else if (productType === 'LocalSim') return ' badge-pill badge badge-light-success'
    else return ' badge-pill badge badge-light-warning'
  }

  const handleStatusColor = (status: string) => {
    if (status == 'New') return ' badge-pill badge badge-light-primary'
    else if (status == 'In_Progress') return ' badge-pill badge badge-light-warning'
    else if (status == 'Resolved') return ' badge-pill badge badge-light-success'
    else if (status == 'Closed') return ' badge-pill badge badge-light-dark'
    else return ' badge-pill badge badge-light'
  }

  // Modal state
  const [showEscalate, setShowEscalate] = useState(false)
  const [showUpdateStatus, setShowUpdateStatus] = useState(false)
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null)

  const openEscalateModal = (ticketId: string) => {
    setActiveTicketId(ticketId)
    setShowEscalate(true)
  }

  const openUpdateStatusModal = (ticketId: string) => {
    setActiveTicketId(ticketId)
    setShowUpdateStatus(true)
  }

  console.log('tickets data:', data)

  return (
    <>
      <KTCard>
        <div className='card-header border-0 pt-6'>
          <div className='card-toolbar d-flex w-100 justify-content-between align-items-center flex-wrap'>
            {/* create ticket */}
            <button
              type='button'
              className='btn btn-primary btn-sm mx-3'
              onClick={() => {
                userId ? navigate(`/tickets/create?userId=${userId}`) : navigate('/tickets/create')
              }}
            >
              Create Ticket
            </button>

            {/* stats row: total, resolved, open */}
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              {/* total tickets */}
              <div className='border border-gray-300 border-dashed rounded min-w-125px px-4  mb-5'>
                <div className='d-flex align-items-center'>
                  <KTSVG
                    path='/media/icons/duotune/communication/com014.svg'
                    className='svg-icon-3 svg-icon-success me-2'
                  />
                  <div className='fs-2 fw-bolder'>{data && data?.total}</div>
                </div>
                <div className='fw-bold fs-6 text-gray-400'>Total Tickets</div>
              </div>

              {/* resolved tickets */}
              {/* <div className='border border-gray-300 border-dashed rounded min-w-125px px-4  mb-5'>
                <div className='d-flex align-items-center'>
                  <KTSVG
                    path='/media/icons/duotune/communication/com014.svg'
                    className='svg-icon-3 svg-icon-success me-2'
                  />
                  <div className='fs-2 fw-bolder'>10</div>
                </div>
                <div className='fw-bold fs-6 text-gray-400'>Resolved</div>
              </div> */}

              {/* open tickets */}
              {/* <div className='border border-gray-300 border-dashed rounded min-w-125px px-4  mb-5'>
                <div className='d-flex align-items-center'>
                  <KTSVG
                    path='/media/icons/duotune/communication/com014.svg'
                    className='svg-icon-3 svg-icon-success me-2'
                  />
                  <div className='fs-2 fw-bolder'>15</div>
                </div>
                <div className='fw-bold fs-6 text-gray-400'>Open</div>
              </div> */}
            </div>
          </div>
        </div>
        <KTCardBody className='py-4'>
          {/* filters below header */}
          {!userId && (
            <div className='mb-5'>
              <TicketsFilters
                value={{...filters, userId: filters.userId || undefined}}
                onChange={(next) => {
                  setFilters(next)
                }}
              />
            </div>
          )}
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-125px' onClick={() => handleSort('id')} style={{cursor: 'pointer'}}>
                    Title {sortBy === 'id' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                  </th>
                  <th className='min-w-125px'>Priority</th>
                  <th className='min-w-125px'>Product</th>
                  <th className='min-w-125px'>Provider</th>
                  <th className='min-w-125px'>Status</th>
                  <th className='min-w-125px'>Created By</th>
                  <th className='min-w-125px'>Assigned To</th>
                  <th className='min-w-125px' onClick={() => handleSort('createdAt')} style={{cursor: 'pointer'}}>
                    Updated {sortBy === 'createdAt' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                  </th>
                  <th className='min-w-100px'>Actions</th>
                </tr>
              </thead>
              <tbody className='text-gray-600 fw-bold'>
                {sortedTickets.length > 0 ? (
                  sortedTickets.map((t) => (
                    <tr key={t.id}>
                      <td>{t.title}</td>
                      <td>
                        <span className={`${handlePriorityColor(t.priority)}`}>{t.priority}</span>
                      </td>
                      <td>
                        <span>{t.productType}</span>
                      </td>
                      <td>
                        <span>{providerIdToName[t.providerId] || t.providerId}</span>
                      </td>
                      <td>
                        <span className={`${handleStatusColor(t.statusName)} `}>
                          {t.statusName}
                        </span>
                      </td>

                      <td>{t.creator?.fullName}</td>
                      <td>{t.assignedTo || 'Unassigned'}</td>
                      <td>{new Date(t.updatedAt).toLocaleString()}</td>
                      <td className='d-flex gap-2'>
                        <button
                          className='btn btn-sm btn-light-primary'
                          onClick={() => navigate(`/accounts/${t.user.id}`)}
                        >
                          View Customer
                        </button>
                        <button
                          className='btn btn-sm btn-light-warning'
                          onClick={() => openEscalateModal(t.id)}
                        >
                          Escalate
                        </button>
                        <button
                          className='btn btn-sm btn-light-success'
                          onClick={() => openUpdateStatusModal(t.id)}
                        >
                          Update Status
                        </button>
                        <button
                          className='btn btn-sm btn-light-info'
                          onClick={() => navigate(`/tickets/${t.id}`)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      No tickets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className='d-flex justify-content-between align-items-center mt-4'>
            <div>
              <span className='text-muted'>
                {data
                  ? `${data.skip + 1}-${Math.min(data.skip + data.take, data.total)} of ${
                      data.total
                    }`
                  : '0-0 of 0'}
              </span>
            </div>
            <div className='btn-group'>
              <button
                className='btn btn-sm btn-light'
                disabled={!data || skip === 0}
                onClick={() => setSkip(Math.max(0, skip - take))}
              >
                Previous
              </button>
              <button
                className='btn btn-sm btn-light'
                disabled={!data || skip + take >= (data?.total || 0)}
                onClick={() => setSkip(skip + take)}
              >
                Next
              </button>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted'>
                {data ? `Page ${Math.floor(skip / take) + 1} of ${totalPages || 1}` : ''}
              </span>
              <div className='d-flex align-items-center'>
                <span className='mx-2 text-muted'>Take:</span>
                <select
                  className='form-select form-select-solid form-select-sm w-100px'
                  value={take}
                  onChange={(e) => {
                    setSkip(0)
                    setTake(parseInt(e.target.value, 10))
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>

      <EscalateTicket
        show={showEscalate}
        ticketId={activeTicketId}
        onClose={() => setShowEscalate(false)}
        onSuccess={() => fetchData()}
      />

      <UpdateTicket
        show={showUpdateStatus}
        ticketId={activeTicketId}
        onClose={() => setShowUpdateStatus(false)}
        onSuccess={() => fetchData()}
      />
    </>
  )
}

export default ListTicket
